import React, { useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { ClickAwayListener, IconButton, InputBase, ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popper, Tooltip, Typography } from '@mui/material';
import SwapHorizontalCircleOutlined from '@mui/icons-material/SwapHorizontalCircleOutlined';
import { useTranslate } from 'react-admin';
import { canSwitchBlockType, filterMenuItems, getConversionTargets, type BlockMenuItem } from './blockMenuItems';
import { newBlockId } from './blockId';

/**
 * "Umwandeln in …" for text-based blocks: keeps the text, swaps the node type
 * (ported from Scilora block-type-switch; MUI Popper instead of a bare div).
 */
export const BlockTypeSwitchButton: React.FC<{ editor: Editor }> = ({ editor }) => {
    const translate = useTranslate();
    const anchor = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const { $from } = editor.state.selection;
    if ($from.depth < 1) return null;
    const nodePos = $from.before(1);
    const node = editor.state.doc.nodeAt(nodePos);
    if (!node || !canSwitchBlockType(node.type.name)) return null;

    const targets = filterMenuItems(getConversionTargets(node.type.name, node.attrs as Record<string, unknown>, translate), query);

    const convert = (target: BlockMenuItem) => {
        const text = node.textContent;
        const json: Record<string, unknown> = {
            type: target.nodeType,
            attrs: { blockId: (node.attrs as Record<string, unknown>).blockId ?? newBlockId(), ...target.attrs },
        };
        if (text) json.content = [{ type: 'text', text }];
        editor.chain().focus().deleteRange({ from: nodePos, to: nodePos + node.nodeSize }).insertContentAt(nodePos, json).run();
        setOpen(false);
        setQuery('');
    };

    return (
        <>
            <Tooltip title={translate('blockEditor.actions.switchType')} placement="left">
                <IconButton ref={anchor} size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((o) => !o)} aria-label={translate('blockEditor.actions.switchType')} sx={{ color: 'var(--oa-text-2)' }}>
                    <SwapHorizontalCircleOutlined fontSize="inherit" />
                </IconButton>
            </Tooltip>
            <Popper open={open} anchorEl={anchor.current} placement="right-start" sx={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Paper elevation={3} sx={{ width: 240, p: 0.5 }}>
                        <InputBase
                            autoFocus
                            fullWidth
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={translate('blockEditor.actions.convertTo')}
                            sx={{ px: 1.5, py: 0.5, fontSize: '0.875rem' }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && targets.length > 0) {
                                    e.preventDefault();
                                    convert(targets[0]);
                                }
                                if (e.key === 'Escape') setOpen(false);
                            }}
                        />
                        <MenuList dense>
                            {targets.length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 1, display: 'block' }}>
                                    {translate('blockEditor.actions.noMatch')}
                                </Typography>
                            )}
                            {targets.map((t) => {
                                const Icon = t.icon;
                                return (
                                    <MenuItem key={t.key} onClick={() => convert(t)}>
                                        <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                                        <ListItemText primary={t.label} />
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};
